/**
 * @file: Invoices.jsx
 * @status: Sovereign Billing System (V2.1.14) - ULTIMATE PRINT PURITY & SMART SEARCH
 * @authority: @CEO - Abdullah Ahmed Al-Zahrani
 * @version: 2026.04.28
 * @description: الملف الكامل والنهائي؛ دمج نظام إخفاء الـ Navbar؛ الترويسة الصناعية؛ الربط مع القاعدة بختم @CEO؛ استخدام logo2026.png.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import hugerLogo from '../assets/logo2026.png'; // المسار المعتمد من رادار الجهاز
import { 
  Printer, Search, Plus, Landmark, Calendar, BarChart3, ShieldCheck, Hash, 
  DollarSign, Activity, School, Globe, Layers, CheckCircle2, AlertCircle, FileText
} from 'lucide-react';
import { 
  message, Table, Tag, Button, Row, Col, Input, Tooltip, Modal, 
  Form, DatePicker, InputNumber, Select, Divider, Statistic, Card 
} from 'antd';
import dayjs from 'dayjs';

const Invoices = () => {
  const { i18n } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [schools, setSchools] = useState([]); // رادار المدارس الذكي
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();
  
  const API_URL = "https://portal.huger.sa/api.php";
  const TAX_RATE = 0.15;

  // --- [ 📡 رادار الفواتير والمدارس السيادي ] ---
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      // جلب رادار الفواتير ببروتوكول POST
      const res = await axios.post(API_URL, 
        { action: 'i_radar' },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (Array.isArray(res.data)) {
        setInvoices(res.data);
      }
      
      // جلب رادار المدارس لنظام البحث الذكي ببروتوكول GET
      const resSchools = await axios.get(`${API_URL}?action=get_schools_list`);
      if (Array.isArray(resSchools.data)) {
        setSchools(resSchools.data);
      }
    } catch (err) {
      console.error("Radar Sync Failure");
      message.error("عائق في رصد الفواتير السحابية للإمبراطورية");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // --- [ 🤖 محرك الترقيم والحسابات التلقائي ] ---
  const handleOpenModal = () => {
    const nextInvoiceId = invoices.length + 1;
    const autoInvoiceNumber = `INV-2026-${String(nextInvoiceId).padStart(3, '0')}`;
    
    setIsModalVisible(true);
    form.setFieldsValue({
      invoice_number: autoInvoiceNumber,
      issue_date: dayjs(),
      status: 'Draft',
      amount: 0,
      tax_amount: 0,
      total_amount: 0
    });
  };

  const onAmountChange = (value) => {
    const baseAmount = value || 0;
    const tax = baseAmount * TAX_RATE;
    const total = baseAmount + tax;
    form.setFieldsValue({
      tax_amount: tax,
      total_amount: total
    });
  };

  // --- [ 🚀 بروتوكول التعميد والرفع السيادي ] ---
  const handleCreateInvoice = async (values) => {
    const hide = message.loading("جاري تعميد الفاتورة سيادياً بختم @CEO...", 0);
    try {
      const res = await axios.post(API_URL, {
        action: 'create_invoice',
        ...values,
        issue_date: values.issue_date.format('YYYY-MM-DD'),
        sealed_by: "@CEO"
      });

      if (res.data.success) {
        message.success("تم الإصدار والتعميد وحفظ البيانات بنجاح ✅");
        setIsModalVisible(false);
        form.resetFields();
        await fetchInvoices(); 
      } else {
        message.error("عائق في الحفظ: " + (res.data.message || "رفض الطلب"));
      }
    } catch (err) {
      message.error("عائق تقني في جسر البيانات السحابي");
    } finally {
      hide();
    }
  };

  const filteredData = invoices.filter(item => 
    item.invoice_number?.toLowerCase().includes(searchText.toLowerCase()) ||
    item.school_name?.toLowerCase().includes(searchText.toLowerCase())
  );

  const currentMonth = dayjs().format('MMMM');
  const monthlyTotal = invoices.reduce((acc, curr) => acc + parseFloat(curr.total_amount || 0), 0);
  const paidCount = invoices.filter(i => i.status === 'Paid').length;

  const columns = [
    { 
      title: 'رقم الفاتورة', 
      dataIndex: 'invoice_number', 
      key: 'invoice_number',
      render: (t) => <span className="text-[#F57C00] font-black italic tracking-tighter">{t}</span> 
    },
    { 
      title: 'الجهة التعليمية', 
      dataIndex: 'school_name', 
      key: 'school_name',
      render: (t) => <span className="font-bold text-white/90">{t || '---'}</span> 
    },
    { 
      title: 'المبلغ الأساسي', 
      dataIndex: 'amount', 
      key: 'amount',
      className: 'hide-on-mobile',
      render: (v) => <span className="text-slate-400 font-bold">{parseFloat(v || 0).toLocaleString()}</span> 
    },
    { 
      title: 'الإجمالي (SAR)', 
      dataIndex: 'total_amount', 
      key: 'total_amount',
      render: (v) => <span className="text-emerald-400 font-black">{parseFloat(v || 0).toLocaleString()}</span> 
    },
    { 
      title: 'الحالة', 
      dataIndex: 'status', 
      key: 'status',
      render: (s) => (
        <Tag color={s === 'Paid' ? 'green' : s === 'Sent' ? 'blue' : 'gold'} className="font-black italic px-3 rounded-md uppercase text-[8px] border-none shadow-sm">
          {s}
        </Tag>
      )
    },
    { 
      title: 'إجراء القيادة', 
      key: 'action',
      className: 'hide-on-print',
      render: (_, record) => (
        <div className="flex gap-2">
          <Tooltip title="طباعة الوثيقة السيادية">
            <Button 
              type="text" 
              onClick={() => window.print()} 
              icon={<Printer size={18} className="text-[#F57C00] hover:scale-110 transition-transform"/>} 
            />
          </Tooltip>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-[#050B14] p-4 md:p-8 text-right font-sans" dir="rtl">
      
      {/* 🛡️ الهيدر والإحصائيات السيادية (مخفي نهائياً في الطباعة) */}
      <div className="max-w-7xl mx-auto mb-10 hide-on-print animate-in fade-in slide-in-from-top-4 duration-700">
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={16}>
            <header className="h-full flex flex-col md:flex-row justify-between items-center bg-[#0A192F] p-8 rounded-[2.5rem] border-2 border-white/5 shadow-2xl gap-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#F57C00]/5 rounded-full blur-3xl"></div>
              <div className="flex items-center gap-6 relative z-10">
                <div className="p-4 bg-gradient-to-br from-[#F57C00] to-orange-900 rounded-2xl shadow-xl shadow-orange-500/20 group hover:rotate-6 transition-transform">
                  <img src={hugerLogo} alt="Logo" className="w-12 h-12 object-contain" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-black text-white italic tracking-tighter leading-none uppercase">مركز الفواتير السيادية</h1>
                  <p className="text-[9px] font-black text-slate-500 uppercase mt-2 tracking-widest italic">HUGER Financial Governance • 2026</p>
                </div>
              </div>
              <div className="flex items-center gap-4 w-full md:w-auto relative z-10">
                <Input 
                  prefix={<Search size={16} className="text-slate-500" />}
                  placeholder="رادار البحث عن سجل..."
                  className="bg-white/5 border-white/10 text-white text-xs rounded-xl hidden lg:flex h-12 w-64 focus:border-[#F57C00] transition-all"
                  onChange={(e) => setSearchText(e.target.value)}
                />
                <button 
                  onClick={handleOpenModal}
                  className="bg-[#F57C00] text-white px-10 h-12 rounded-xl font-black text-[10px] hover:bg-orange-600 transition-all flex items-center gap-3 shadow-xl shadow-orange-500/20 border-none cursor-pointer uppercase italic whitespace-nowrap active:scale-95"
                >
                  <Plus size={18}/> إصدار فاتورة جديدة
                </button>
              </div>
            </header>
          </Col>
          <Col xs={24} lg={8}>
            <div className="h-full bg-gradient-to-br from-emerald-600/10 to-transparent p-8 rounded-[2.5rem] border border-emerald-500/20 flex flex-col justify-center relative overflow-hidden group shadow-2xl">
              <div className="absolute -right-4 -bottom-4 opacity-5 group-hover:opacity-10 transition-all">
                <BarChart3 size={140} className="text-emerald-500" />
              </div>
              <span className="text-[11px] font-black text-emerald-500 uppercase tracking-widest mb-2 flex items-center gap-2 italic">
                <Activity size={14}/> إجمالي شهر {currentMonth}
              </span>
              <h2 className="text-4xl font-black text-white tracking-tighter italic leading-none">
                {monthlyTotal.toLocaleString()} <span className="text-xs text-emerald-500 font-bold ml-1">SAR</span>
              </h2>
              <div className="mt-4 flex gap-4 text-[9px] font-black uppercase text-slate-500 italic">
                <span>تم تحصيل: {paidCount} فواتير</span>
                <span className="text-emerald-500">معدل التحصيل: {invoices.length > 0 ? ((paidCount/invoices.length)*100).toFixed(0) : 0}%</span>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* 📄 منطقة السجلات المالية - الترويسة السيادية والطباعة النظيفة */}
      <main className="max-w-7xl mx-auto bg-[#0A192F]/60 backdrop-blur-xl border border-white/10 rounded-[3.5rem] p-6 md:p-10 shadow-2xl print-container relative transition-all">
        
        {/* 🏛️ الترويسة الرسمية الصناعية (تظهر فقط عند الطباعة) */}
        <div className="only-print w-full mb-10 border-b-2 border-black pb-10">
          <div className="flex justify-between items-start">
            <div className="text-right">
              <div className="flex items-center gap-4 mb-4">
                <img src={hugerLogo} alt="Seal" className="w-16 h-16 grayscale" />
                <div>
                  <h1 className="text-3xl font-black mb-1">ضخامة للصناعة HUGER</h1>
                  <p className="text-[12px] font-bold">رقم السجل التجاري: 4032246821</p>
                </div>
              </div>
              <div className="mt-6 text-[10px] leading-relaxed font-bold uppercase tracking-wider space-y-1">
                <p>INTERACTIVE EXHIBITIONS. CONCEPT . DESIGN . FABRICATION</p>
                <p>Kingdom of Saudi Arabia • Taif Postal Code 3217-26513</p>
                <p>Tel: 0508556581 • E-mail: info@huger.sa</p>
                <p className="text-blue-800 underline">https://huger.sa</p>
              </div>
            </div>
            
            <div className="text-left flex flex-col items-end">
               <div className="border-4 border-black p-6 mb-6 text-center w-64">
                  <h2 className="text-2xl font-black tracking-widest uppercase italic">INVOICE</h2>
                  <p className="text-[11px] font-black mt-1">Sovereign System V2.1.14</p>
               </div>
               <div className="text-[11px] font-bold space-y-2 text-right">
                  <p>تاريخ الإصدار: <span className="font-mono">{dayjs().format('YYYY-MM-DD')}</span></p>
                  <p>معرف العميل: <span className="font-mono">{invoices[0]?.school_id || '----'}</span></p>
                  <p>الرقم الضريبي: <span className="font-mono">310000000000003</span></p>
               </div>
            </div>
          </div>
        </div>

        {/* الجدول الرئيسي */}
        <Table 
          dataSource={filteredData} 
          columns={columns} 
          loading={loading} 
          rowKey="id" 
          pagination={{ pageSize: 10, className: 'hide-on-print', size: 'small', position: ['bottomCenter'] }}
          className="huger-sovereign-table" 
        />
        
        {/* تذييل الطباعة الرسمي بختم @CEO */}
        <div className="only-print mt-20 flex justify-between items-end border-t-2 border-black pt-12">
            <div className="text-[10px] opacity-60 font-mono italic">
              * تم إصدار هذه الوثيقة آلياً بختم القيادة العليا لشركة ضخامة للصناعة.<br/>
              * جميع الحقوق محفوظة لـ HUGER Digital Empire 2026.
            </div>
            <div className="text-center">
              <p className="font-black mb-4 text-[12px] uppercase tracking-widest italic">يعمد سيادياً بختم القيادة العليا</p>
              <div className="border-[5px] border-black p-6 inline-block transform -rotate-3 italic font-serif text-3xl font-black shadow-lg">
                @CEO SEAL
              </div>
              <p className="mt-4 font-black text-lg italic underline decoration-black underline-offset-4">
                Abdullah Ahmed Al-Zahrani
              </p>
            </div>
        </div>
      </main>

      {/* 🚀 نافذة الإدخال الذكية */}
      <Modal
        title={
          <div className="text-white font-black italic flex items-center gap-3 uppercase tracking-widest py-2">
            <ShieldCheck className="text-[#F57C00]" size={24}/> 
            <span>تعميد فاتورة سيادية جديدة</span>
          </div>
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
        centered
        className="huger-modal-sovereign"
      >
        <Form form={form} layout="vertical" onFinish={handleCreateInvoice} className="mt-6">
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name="invoice_number" label="رقم الفاتورة (ترقيم آلي سيادي)">
                <Input readOnly className="huger-input-fixed text-center font-mono" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="school_id" label="اختيار الجهة التعليمية (رادار المدارس)" rules={[{ required: true, message: 'يرجى اختيار العميل' }]}>
                <Select 
                  className="huger-select-field" 
                  placeholder="ابحث عن المدرسة في النظام..."
                  showSearch
                  optionFilterProp="children"
                >
                  {schools.map(school => (
                    <Select.Option key={school.id} value={school.id}>
                      {school.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider className="border-white/10 my-6" />

          <Row gutter={24}>
            <Col span={8}>
              <Form.Item name="amount" label="المبلغ الأساسي (SAR)" rules={[{ required: true }]}>
                <InputNumber 
                  placeholder="0.00" 
                  className="huger-input-field w-full"
                  onChange={onAmountChange}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="tax_amount" label="الضريبة السيادية (15%)">
                <InputNumber readOnly className="huger-input-fixed w-full text-emerald-500 font-bold" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="total_amount" label="الإجمالي النهائي المعمد">
                <InputNumber readOnly className="huger-input-fixed w-full text-emerald-400 font-black bg-emerald-500/10" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name="issue_date" label="تاريخ الإصدار">
                <DatePicker className="huger-input-field w-full" format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="حالة التدفق المالي">
                <Select className="huger-select-field">
                  <Select.Option value="Draft">Draft (مسودة داخلية)</Select.Option>
                  <Select.Option value="Sent">Sent (تم الإرسال للعميل)</Select.Option>
                  <Select.Option value="Paid">Paid (تم التحصيل بنجاح)</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <div className="mt-8">
            <Button 
              type="primary" 
              htmlType="submit" 
              block 
              className="bg-[#F57C00] hover:bg-orange-600 border-none font-black h-16 rounded-2xl shadow-xl text-sm uppercase italic transition-all active:scale-95"
            >
              تعميد وإصدار الفاتورة بختم القيادة العليا @CEO
            </Button>
          </div>
        </Form>
      </Modal>

      {/* 🏗️ التحصين البرمجي للبصريات (CSS Control) */}
      <style>{`
        .huger-input-field, .huger-input-fixed, .ant-select-selector { 
          background: #050B14 !important; 
          border: 1px solid rgba(255,255,255,0.1) !important; 
          color: white !important; 
          height: 56px !important; 
          border-radius: 14px !important; 
          display: flex !important; 
          align-items: center !important; 
        }
        .huger-input-field:focus, .huger-input-field:hover { 
          border-color: #F57C00 !important; 
          box-shadow: 0 0 15px rgba(245,124,0,0.3) !important; 
        }
        .huger-input-fixed { border-color: rgba(245,124,0,0.3) !important; color: #F57C00 !important; font-weight: 900 !important; }
        .ant-input-number-input, .ant-picker-input > input, .ant-select-selection-item { 
          color: white !important; 
          font-weight: 900 !important; 
        }
        .ant-modal-content { 
          background: #0A192F !important; 
          border: 2px solid rgba(245,124,0,0.5) !important; 
          border-radius: 3rem !important; 
          padding: 3rem !important; 
        }
        .ant-form-item-label > label { color: #64748b !important; font-weight: 900 !important; font-size: 10px !important; text-transform: uppercase !important; letter-spacing: 1px; }
        
        .huger-sovereign-table .ant-table { background: transparent !important; color: white !important; }
        .huger-sovereign-table .ant-table-thead > tr > th { 
          background: rgba(255,255,255,0.03) !important; 
          color: #64748b !important; 
          border-bottom: 2px solid rgba(245,124,0,0.2) !important; 
          font-weight: 900 !important; 
          text-transform: uppercase !important;
          font-size: 10px;
        }
        .huger-sovereign-table .ant-table-tbody > tr > td { border-bottom: 1px solid rgba(255,255,255,0.05) !important; }
        .huger-sovereign-table .ant-table-tbody > tr:hover > td { background: rgba(245,124,0,0.05) !important; }
        
        .only-print { display: none; }

        @media print {
          header, .hide-on-print, .ant-pagination, .ant-btn { display: none !important; }
          body { background: white !important; color: black !important; padding: 0 !important; }
          .only-print { display: block !important; }
          .print-container { 
            background: white !important; 
            border: none !important; 
            color: black !important; 
            width: 100% !important; 
            padding: 0 !important; 
            margin: 0 !important;
            box-shadow: none !important;
            scale: 1 !important;
            transform: none !important;
          }
          .ant-table { color: black !important; }
          .ant-table-thead > tr > th { background: #f0f0f0 !important; color: black !important; border-bottom: 2px solid black !important; }
          .ant-table-tbody > tr > td { border-bottom: 1px solid #ccc !important; color: black !important; }
          .text-white, .text-emerald-400, .text-[#F57C00], .text-white/90 { color: black !important; font-weight: bold !important; }
          @page { size: A4; margin: 15mm; }
        }
      `}</style>
    </div>
  );
};

export default Invoices;